/**
 * Collateral API client — template listing + server-side render.
 *
 * The render endpoint returns raw file bytes (PNG/PDF), so those calls ask
 * axios for a Blob rather than the usual JSON envelope.
 */
import { apiV1 } from '@/api/client';

export const collateralApi = {
  listTemplates: async () => {
    const res = await apiV1.get('/collateral/templates');
    return res.data;
  },

  getTemplate: async (id) => {
    const res = await apiV1.get(`/collateral/templates/${id}`);
    return res.data;
  },

  /**
   * Render a template to a Blob. `format` is 'png' or 'pdf'. `agentId` is
   * optional — without it, only the template's static content renders.
   */
  render: async ({ templateId, subjectType = 'agent', subjectId = null, agentId = null, format = 'png', overrides = {} }) => {
    const res = await apiV1.post(
      '/collateral/render',
      { template_id: templateId, subject_type: subjectType, subject_id: subjectId ?? agentId, agent_id: agentId, format, overrides },
      { responseType: 'blob' }
    );
    return res.data;
  },

  createTemplate: async (template) => {
    const res = await apiV1.post('/collateral/templates', template);
    return res.data;
  },

  updateTemplate: async (id, template) => {
    const res = await apiV1.put(`/collateral/templates/${id}`, template);
    return res.data;
  },

  deleteTemplate: async (id) => {
    const res = await apiV1.delete(`/collateral/templates/${id}`);
    return res.data;
  },

  /** Copy a template (system or custom) into a new editable custom template. */
  duplicateTemplate: async (id) => {
    const res = await apiV1.post(`/collateral/templates/${id}/duplicate`);
    return res.data;
  },

  /** Fetch a template's gallery thumbnail as a PNG Blob. */
  thumbnail: async (id) => {
    const res = await apiV1.get(`/collateral/templates/${id}/thumbnail`, { responseType: 'blob' });
    return res.data;
  },

  /**
   * Browse the shared media library for images (reused by the editor's image
   * picker). Returns { items, total }. Goes through apiV1 so SSO cookie auth
   * applies (unlike the bearer-only mediaApi).
   */
  listImages: async ({ search = '', tags = null, skip = 0, limit = 40 } = {}) => {
    const params = { media_type: 'image', skip, limit };
    if (search) params.search = search;
    if (tags && tags.length) params.tags = tags.join(',');
    const res = await apiV1.get('/media/', { params });
    return { items: res.data?.data || [], total: res.data?.meta?.pagination?.total || 0 };
  },

  /** List uploaded brand fonts as [{ family: 'custom:<id>', label }]. */
  listFonts: async () => {
    const res = await apiV1.get('/collateral/fonts');
    return res.data?.data?.items || [];
  },

  /** Fetch an uploaded font file as a Blob (for canvas FontFace preview). */
  fontFile: async (id) => {
    const res = await apiV1.get(`/collateral/fonts/${id}/file`, { responseType: 'blob' });
    return res.data;
  },

  /** Upload a .ttf/.otf brand font; returns { family, label }. */
  uploadFont: async (file, label = '') => {
    const form = new FormData();
    form.append('file', file);
    if (label) form.append('label', label);
    const res = await apiV1.post('/collateral/fonts', form, { headers: { 'Content-Type': undefined } });
    return res.data?.data;
  },

  /** Upload an image into the shared media library (collateral folder). */
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('folder_path', 'collateral');
    form.append('optimize_for_web', 'true');
    form.append('output_format', 'webp');
    // Content-Type undefined → browser sets multipart boundary (apiV1 defaults to JSON).
    const res = await apiV1.post('/media/upload', form, { headers: { 'Content-Type': undefined } });
    return res.data?.data; // { id, url, width, height, ... }
  },

  /**
   * Render an unsaved template definition to a Blob (editor live preview).
   * `format` defaults to 'png'; pass 'mp4' to preview animation.
   */
  renderPreview: async ({ template, subjectType = 'agent', subjectId = null, agentId = null, overrides = {}, format = 'png' }) => {
    const res = await apiV1.post(
      '/collateral/render-preview',
      { template, subject_type: subjectType, subject_id: subjectId ?? agentId, agent_id: agentId, overrides, format },
      { responseType: 'blob' }
    );
    return res.data;
  },

  /**
   * Batch-render a template for a whole office (or an explicit agent list).
   * Returns { blob (zip), count, truncated }.
   */
  batch: async ({ templateId, officeId = null, agentIds = null, format = 'png' }) => {
    const res = await apiV1.post(
      '/collateral/batch',
      { template_id: templateId, office_id: officeId, agent_ids: agentIds, format },
      { responseType: 'blob' }
    );
    return {
      blob: res.data,
      count: Number(res.headers['x-item-count'] || 0),
      truncated: res.headers['x-truncated'] === '1',
    };
  },
};

/** The curated dynamic-field palette shown in the editor. Tokens match the
 *  backend binding resolver (agent/office/listing). Image tokens are flagged so
 *  the UI only offers them for image layers.
 *
 *  `alchemy` (optional) maps a token to its canonical Alchemy PDFlib part for the
 *  push-to-Alchemy dynamic-template bridge:
 *    part  = alc.refMediaItemPart.IntMediaItemPartId (the mapping FK)
 *    block = the PDFlib block name LinkHub emits (nvcAutoMapToBlock — auto-binds
 *            to this part on import)
 *    type  = block subtype (Text | Image | Logo)
 *  Tokens without `alchemy` have no canonical Alchemy part equivalent
 *  (e.g. title, awards, office type, region/partnership) and are LinkHub-only —
 *  they render in LinkHub-native templates but won't map when pushing a
 *  dynamic-PDF template. Single-agent parts are used here; multi-agent (1..N
 *  agents on one template) is a future addition. Descriptors sourced from the
 *  live catalog (alc.refMediaItemPart) — see Alchemy-PDF-parts-catalog.txt. */
export const DYNAMIC_FIELDS = [
  {
    group: 'Agent',
    items: [
      { token: 'agent.name', label: 'Full name', alchemy: { part: 2006, block: 'SingleAgentDisplayName1', type: 'Text' } },
      { token: 'agent.title', label: 'Title' },
      { token: 'agent.phone', label: 'Phone', alchemy: { part: 2003, block: 'SingleAgentMobileNumber1', type: 'Text' } },
      { token: 'agent.email', label: 'Email', alchemy: { part: 2004, block: 'SingleAgentEmailAddress1', type: 'Text' } },
      { token: 'agent.awards', label: 'Awards' },
    ],
  },
  {
    group: 'Agent photo',
    image: true,
    items: [
      { token: 'agent.image.portrait', label: 'Lifestyle portrait', alchemy: { part: 2021, block: 'SingleAgentLifestylePortraitImage1', type: 'Image' } },
      { token: 'agent.image.high_res', label: 'Profile photo' },
      { token: 'agent.image.landscape', label: 'Lifestyle landscape', alchemy: { part: 2019, block: 'SingleAgentLifestyleImage1', type: 'Image' } },
      { token: 'agent.image.business_card', label: 'Business card', alchemy: { part: 2026, block: 'SingleAgentBusinessCardImage1', type: 'Image' } },
      { token: 'agent.image.calling_card', label: 'Calling card' },
      { token: 'agent.image.sale_boards', label: 'Sale boards', alchemy: { part: 3171, block: 'AgentSaleBoardsImage', type: 'Image' } },
    ],
  },
  {
    group: 'Office',
    items: [
      { token: 'office.name', label: 'Office name', alchemy: { part: 2, block: 'OfficeName1', type: 'Text' } },
      { token: 'office.address', label: 'Address', alchemy: { part: 1, block: 'OfficeDetail1', type: 'Text' } },
      { token: 'office.phone', label: 'Office phone', alchemy: { part: 3, block: 'OfficeTelNumber1', type: 'Text' } },
      { token: 'office.email', label: 'Office email', alchemy: { part: 6, block: 'OfficeEmailAddress1', type: 'Text' } },
      { token: 'office.type', label: 'Type (branch/franchise)' },
    ],
  },
  {
    group: 'Region',
    items: [
      { token: 'region.name', label: 'Region name' },
      { token: 'region.code', label: 'Region code' },
    ],
  },
  {
    group: 'Partnership',
    items: [
      { token: 'partnership.name', label: 'Partnership name' },
      { token: 'partnership.code', label: 'Partnership code' },
    ],
  },
  {
    group: 'Listing',
    items: [
      { token: 'listing.heading', label: 'Heading', alchemy: { part: 3067, block: 'Property1HeaderText1', type: 'Text' } },
      { token: 'listing.price', label: 'Price', alchemy: { part: 3054, block: 'Property1Price', type: 'Text' } },
      { token: 'listing.location', label: 'Location', alchemy: { part: 3052, block: 'Property1Area1', type: 'Text' } },
      { token: 'listing.full_location', label: 'Full address', alchemy: { part: 3051, block: 'Property1Address1', type: 'Text' } },
      { token: 'listing.bedrooms', label: 'Bedrooms', alchemy: { part: 3096, block: 'Property1BedFormat1', type: 'Text' } },
      { token: 'listing.bathrooms', label: 'Bathrooms', alchemy: { part: 3092, block: 'Property1BathFormat1', type: 'Text' } },
      { token: 'listing.parking', label: 'Parking', alchemy: { part: 3123, block: 'Property1Parking1', type: 'Text' } },
      { token: 'listing.type', label: 'Property type', alchemy: { part: 3058, block: 'Property1Type1', type: 'Text' } },
      { token: 'listing.status', label: 'Status (On Show / Under Offer)', alchemy: { part: 3122, block: 'Property1OnShowDetail1', type: 'Text' } },
      { token: 'listing.ref', label: 'Listing ref', alchemy: { part: 3121, block: 'Property1ListingRef1', type: 'Text' } },
    ],
  },
  {
    group: 'Listing photo',
    image: true,
    items: [
      { token: 'listing.image.main', label: 'Main photo', alchemy: { part: 3001, block: 'Property1Image1', type: 'Image' } },
    ],
  },
];

/**
 * Turn a set of selected layers into a parametric RepeatGroup: the layers become
 * the repeating cell (coords made relative to the group region = their bounding
 * box), bound to the subject's agents/properties list. One authored group
 * compiles to the N base-PDF variants on export. See the bridge design, §3D.
 */
export function buildRepeatGroup(layers, opts = {}) {
  if (!layers || layers.length === 0) return null;
  const { bind = 'agents', layout = 'columns', gap = 12, minCount = 1, maxCount = 3 } = opts;
  const x0 = Math.min(...layers.map((l) => l.x));
  const y0 = Math.min(...layers.map((l) => l.y));
  const x1 = Math.max(...layers.map((l) => l.x + l.width));
  const y1 = Math.max(...layers.map((l) => l.y + l.height));
  return {
    id: `rpt_${Math.random().toString(36).slice(2, 12)}`,
    bind,
    layout,
    gap,
    min_count: minCount,
    max_count: maxCount,
    region: { x: x0, y: y0, width: x1 - x0, height: y1 - y0 },
    cell: layers.map((l) => ({ ...l, x: l.x - x0, y: l.y - y0 })),
  };
}
